<template>
  <div class="combinations-header row">
    <div class="col flex items-center no-wrap">
      <h3 class="q-my-none"> Possible combinations </h3>

      <!-- View selection -->
      <mi-btn-group v-if="false" class="text-no-wrap q-ml-lg" outline>
        <mi-btn color="accent"> Table View </mi-btn>
        <mi-btn color="accent" outline disable> Tree View </mi-btn>
      </mi-btn-group>
    </div>

    <div class="col-auto flex items-center justify-end no-wrap q-ml-md">
      <!-- Combinations drop down -->
      <div class="table-display__wrapper">
        <mi-btn-dropdown class="table-display-dropdown" flat label="Table Display">
          <div class="dropdown-wrapper dropdown-wrapper-gap">
            <span class="text-family-condensed filter-text text-bold self-start"> Filtering options </span>
            <mi-list>
              <mi-list-item class="filter-wrapper">
                <mi-list-item-section class="filter-switch">
                  <div
                    v-for="layoutOption in FILTERING_OPTIONS"
                    :key="layoutOption.title"
                    class="switch-wrapper"
                  >
                    <mi-switch
                      v-model="layoutOption.state"
                      @click="handleOptionsFilterToggle()"
                    >
                    </mi-switch>
                    <span class="text-body2 text-weight-regular"> {{ layoutOption.label }} </span>
                  </div>
                </mi-list-item-section>
              </mi-list-item>
            </mi-list>
            <span class="text-family-condensed filter-text text-bold self-start"> Table layout </span>
            <mi-list>
              <mi-list-item class="filter-wrapper">
                <mi-list-item-section class="filter-switch">
                  <div class="switch-wrapper">
                    <mi-switch
                      v-model="LAYOUT_OPTIONS.showCodeId.state"
                      @click="handleLayoutOptionsToggle(
                        LAYOUT_OPTIONS.showCodeId.ref, LAYOUT_OPTIONS.showName.ref, true
                      )"
                    >
                    </mi-switch>
                    <span class="text-body2 text-weight-regular"> {{ LAYOUT_OPTIONS.showCodeId.title }} </span>
                  </div>
                  <div class="switch-wrapper">
                    <mi-switch
                      v-model="LAYOUT_OPTIONS.showName.state"
                      @click="handleLayoutOptionsToggle(
                        LAYOUT_OPTIONS.showName.ref, LAYOUT_OPTIONS.showCodeId.ref, true
                      )"
                    >
                    </mi-switch>
                    <span class="text-body2 text-weight-regular"> {{ LAYOUT_OPTIONS.showName.title }} </span>
                  </div>
                </mi-list-item-section>
              </mi-list-item>
            </mi-list>
          </div>
        </mi-btn-dropdown>
      </div>

      <!-- Save for later button -->
      <mi-btn class="q-ml-sm" :disable="isTableSaved" flat @click="toggleDialog"> Save for later </mi-btn>

      <!-- Auto swap columns button -->
      <mi-btn class="q-ml-sm hidden" @click="$emit('click:auto-swap')"> Auto Swap Columns </mi-btn>

      <mi-btn-dropdown
        class="text-family-condensed table-display-dropdown q-ml-md"
        label="Export data"
        split
        @click="exportData"
      >
        <div class="dropdown-wrapper">
          <span class="filter-text text-bold q-mb-md self-start"> Exporting Settings </span>
          <mi-list>
            <mi-list-item class="filter-wrapper">
              <mi-list-item-section class="filter-switch">
                <div class="switch-wrapper">
                  <mi-switch
                    v-model="LAYOUT_EXCEL.showCodeId.state"
                    @click="handleLayoutOptionsToggle(LAYOUT_EXCEL.showCodeId.ref, LAYOUT_EXCEL.showName.ref)"
                  >
                  </mi-switch>
                  <span class="text-body2 text-weight-regular"> {{ LAYOUT_EXCEL.showCodeId.title }} </span>
                </div>
                <div class="switch-wrapper">
                  <mi-switch
                    v-model="LAYOUT_EXCEL.showName.state"
                    @click="handleLayoutOptionsToggle(LAYOUT_EXCEL.showName.ref, LAYOUT_EXCEL.showCodeId.ref)"
                  >
                  </mi-switch>
                  <span class="text-body2 text-weight-regular"> {{ LAYOUT_OPTIONS.showName.title }} </span>
                </div>
                <div class="switch-wrapper">
                  <mi-switch
                    v-model="LAYOUT_EXCEL.splitCodeIdAndName.state"
                    @click="handleLayoutOptionsToggle(LAYOUT_EXCEL.splitCodeIdAndName.ref, [
                      LAYOUT_EXCEL.showName.ref,
                      LAYOUT_EXCEL.showCodeId.ref
                    ])"
                  >
                  </mi-switch>
                  <span class="text-body2 text-weight-regular"> {{ LAYOUT_EXCEL.splitCodeIdAndName.title }} </span>
                </div>
              </mi-list-item-section>
            </mi-list-item>
          </mi-list>
          <mi-btn
            size="md"
            :loading="isExporting"
            class="q-ml-sm q-mt-lg export-button"
            color="accent"
            icon-right="download"
            icon-right-size="14px"
            @click="exportData"
          >
            Export Data
          </mi-btn>
        </div>
      </mi-btn-dropdown>

      <div class="flex items-center no-wrap q-ml-md">
        <!-- Go back button -->
        <mi-btn :to="goBackNavigationLink" icon="close" icon-type fab flat></mi-btn>
      </div>
    </div>

    <!-- Resize observer component is provided from parent here -->
    <slot></slot>
  </div>
  <div class="combinations-info-header row">
    <div v-if="productModelBusinessName" class="combinations-info-product-model">
      <span class="text-family-condensed text-weight-bold text-uppercase">Product Model</span>
      <span>{{ productModelBusinessName }}</span>
    </div>
    <div v-if="planningPeriodRange" class="combinations-info-pp">
      <span class="text-family-condensed text-weight-bold text-uppercase">Planning Period</span>
      <span>{{ planningPeriodRange }}</span>
    </div>
  </div>
  <saved-tables-dialog
    v-model="showDialog"
    :item="{ correlationId }"
    :on-save-callback="onSaveCallback"
    :on-save-in-progress="saveCombinationNameInProgress"
    :is-table-saved="isTableSaved"
  ></saved-tables-dialog>
</template>

<script>
  import { useStore } from 'vuex'
  import { RESULTS_FILTER } from '@/constants'
  import MiListItem from '@/packages/@mi-library/MiList/MiListItem.vue'
  import cloneDeep from 'lodash.clonedeep'
  import { getVariantTreeTableHeader, saveVariantTreeCombination } from '@/api'
  import SavedTablesDialog from '@/components/saved-tables/SavedTablesDialog.vue'
  import recordAnalytics from '@/utils/analytics/recordAnalytics'
  import { VM_SAVED_FOR_LATER_CALCULATION } from '@/utils/analytics/constants'
  import notify from '@/utils/notify'
  import { BASIC_INFO } from '@/plugins/quasar/notifications/notificationTypes'
  import { SAVED_TABLES_ROUTE } from '@/router/routeNames'
  import { handleOptionsToggle } from '@/utils/optionsToggle'
  import * as analyticsConstants from '@/utils/analytics/constants'
  import { computed, onBeforeMount, reactive, ref } from 'vue'
  import { useRoute } from 'vue-router'
  import { useCombinationsOptions } from '../utils/composables/useCombinationsOptions'

  const LAYOUT_FILTER_OPTIONS = {
    showCodeId: {
      title: 'Display ID',
      ref: 'showCodeId',
      id: 'SHOW_CODE_ID',
      state: true
    },
    showName: {
      title: 'Display Name',
      id: 'SHOW_NAME',
      ref: 'showName',
      state: true
    }
  }

  export default {
    name: 'CombinationsHeader',
    components: {
      SavedTablesDialog,
      MiListItem
    },
    props: {
      correlationId: {
        type: String,
        required: true
      },
      isTableSaved: {
        type: Boolean,
        required: false,
        default: false
      },
      isExporting: {
        type: Boolean,
        required: false,
        default: false
      },
      searchString: {
        type: String,
        required: false,
        default: ''
      },
      showAmbiguous: {
        type: Boolean,
        required: false,
        default: true
      },
      onlyIncomplete: {
        type: Boolean,
        required: false,
        default: false
      }
    },
    emits: [
      'click:auto-swap',
      'click:export-data',
      'update:show-results', '@update:isTableSaved'
    ],
    setup(props, { emit }) {
      const planningPeriodRange = ref(null)
      const tableName = ref(null)
      const productModelBusinessName = ref(null)
      const disableToggleIfNotComponentsAvailable = ref(true)
      const saveCombinationNameInProgress = ref(false)
      const showDialog = ref(false)
      const { aliasesAsRequestParams } = useCombinationsOptions()
      const store = useStore()
      const selectedResultType = computed(() => store.state.search.selectedResultType)
      const route = useRoute()
      const LAYOUT_OPTIONS = reactive(cloneDeep(LAYOUT_FILTER_OPTIONS))

      const LAYOUT_EXCEL = reactive({
        ...LAYOUT_FILTER_OPTIONS,
        splitCodeIdAndName: {
          title: 'Split name from ID cells',
          id: 'SPLIT_CODE_ID_AND_NAME',
          ref: 'splitCodeIdAndName',
          state: false
        }
      })

      const FILTERING_OPTIONS = reactive({
        showIncomplete: {
          title: 'showIncomplete',
          state: true,
          label: 'Incomplete'
        },
        showAmbiguous: {
          title: 'showAmbiguous',
          state: true,
          label: 'Ambiguous'
        },
        showValidResults: {
          title: 'showValidResults',
          state: true,
          label: 'Valid Results'
        }
      })

      const goBackNavigationLink = computed(() => {
        if (route.params?.previousPage === SAVED_TABLES_ROUTE.name) {
          return { name: SAVED_TABLES_ROUTE.name }
        }

        const query = { resultType: selectedResultType.value }

        if (props.searchString) {
          query.searchString = props.searchString
        }

        return { name: 'search', query }
      })

      const getTableHeader = async () => {
        const { header: tableHeader, name } = await getVariantTreeTableHeader({
          aliases: aliasesAsRequestParams(),
          correlationId: props.correlationId
        })

        tableName.value = name
        const componentsHeaders = tableHeader.filter(header => header.type === 'component')
        disableToggleIfNotComponentsAvailable.value = !componentsHeaders.length
      }

      const handleOptionsFilterToggle = () => {
        emit('update:show-results', {
          ambiguous: FILTERING_OPTIONS.showAmbiguous.state,
          incomplete: FILTERING_OPTIONS.showIncomplete.state,
          valid: FILTERING_OPTIONS.showValidResults.state
        })
      }

      const updateResultsFilter = async ({ update, isExcel = false }) => {
        await store.commit(`search/UPDATE_RESULTS_FILTER_${ isExcel ? 'EXCEL' : 'TABLE' }`, update)
      }

      const triggerLayoutOptionsEvents = (layoutOptions, isTableDisplay) => {
        // eslint-disable-next-line no-unused-vars
        const updates = Object.entries(layoutOptions).map(([key, { id, state }]) => ({
          filterType: RESULTS_FILTER[id],
          newVal: state
        }))

        updates.forEach(update => (
          isTableDisplay
            ? updateResultsFilter({ update }) : updateResultsFilter({ update, isExcel: true })
        ))
      }

      const handleLayoutOptionsToggle = (currentToggle, toggleToBeEnabled, isTableDisplay = false) => {
        const layoutOptions = isTableDisplay ? LAYOUT_OPTIONS : LAYOUT_EXCEL

        handleOptionsToggle(layoutOptions, currentToggle, toggleToBeEnabled)

        triggerLayoutOptionsEvents(layoutOptions, isTableDisplay)
      }

      const exportData = () => {
        emit('click:export-data', tableName.value)
      }

      const toggleDialog = () => {
        showDialog.value = !showDialog.value
        recordAnalytics(analyticsConstants.VM_POSSIBLE_COMBINATIONS_SAVE_FOR_LATER)
      }

      const tableIsSavedNotify = () => {
        notify({
          content: 'Table is saved in “Saved Tables”.',
          type: BASIC_INFO,
          position: 'bottom-right'
        })
      }

      const onSaveCallback = async ({ correlationId, name }) => {
        saveCombinationNameInProgress.value = true
        try {
          await saveVariantTreeCombination({ correlationId, name })
          recordAnalytics(VM_SAVED_FOR_LATER_CALCULATION)
        }
        catch (err) {
          throw new Error(
            `An error occurred trying to save the tables with the correlationId: ${ correlationId }`
          )
        }
        finally {
          tableIsSavedNotify()
          emit('@update:isTableSaved', true)
          saveCombinationNameInProgress.value = false
          showDialog.value = false
        }
      }

      onBeforeMount(() => {
        if (!route.query) return
        if (route.query.pp) {
          planningPeriodRange.value = atob(route.query.pp)
        }
        if (route.query.productModelBusinessName) {
          productModelBusinessName.value = atob(route.query.productModelBusinessName)
        }
        else if (route.query.exportName) {
          productModelBusinessName.value = route.query.exportName
        }

        getTableHeader()
      })

      return {
        planningPeriodRange,
        tableName,
        productModelBusinessName,
        disableToggleIfNotComponentsAvailable,
        LAYOUT_OPTIONS,
        LAYOUT_EXCEL,
        FILTERING_OPTIONS,
        saveCombinationNameInProgress,
        showDialog,
        goBackNavigationLink,
        handleOptionsFilterToggle,
        exportData,
        handleLayoutOptionsToggle,
        toggleDialog,
        onSaveCallback
      }
    }
  }
</script>

<style lang="scss">
  .combinations-info-header {
    flex-direction: column;

    span {
      padding-right: .5rem;
    }
  }

  .dropdown-wrapper {
    padding: 8px;
    padding-right: 16px;
    padding-bottom: 16px;
    display: flex;
    flex-direction: column;
  }

  .dropdown-wrapper-gap {
    gap: 16px;
  }

  .filter-switch {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 16px;
  }

  .switch-wrapper {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    padding: 0;
    height: 24px;
  }

  .filter-text {
    font-size: 12px;
    font-style: normal;
    font-weight: 700;
    line-height: 15.6px;
    padding-left: 8px;
  }

  .filter-text:first-child {
    padding-top: 10px;
  }

  .filter-wrapper {
    padding-left: 8px;
    padding-right: 8px;
  }

  .export-button {
    width: 248px;
    height: 32px;
  }

  .only-incomplete .q-toggle__inner--falsy .q-toggle__track {
    // stylelint-disable declaration-no-important
    background-color: #5b6f85 !important;
  }

  .show-ambiguous .q-toggle__track {
    background-color: $mi-silver-400 !important;
  }

  .show-ambiguous-span {
    color: $mi-silver-400;
  }
</style>
